<template>
  <div class="qr-code-stream-wrapper">
    <video ref="video" autoplay playsinline muted class="qr-video"></video>
    <canvas ref="canvas" class="qr-canvas"></canvas>
  </div>
</template>

<script>
  import jsQR from 'jsqr';

  /**
   * Custom QR Code Stream Component
   * Replicates vue-qrcode-reader functionality using native getUserMedia and jsQR library
   * 
   * @emits init - Emits a promise when camera initialization starts
   * @emits camera-on - Emits the MediaStream when camera is ready
   * @emits decode - Emits the decoded QR code data string
   */
  export default {
    name: 'QrcodeStream',
    props: {
      constraints: {
        type: Object,
        default: () => ({ video: { facingMode: 'environment' } })
      }
    },
    data() {
      return {
        currentStream: null,
        scanInterval: null,
        isScanning: false,
        isDestroyed: false
      };
    },
    methods: {
      /**
       * Start the camera stream and begin scanning
       */
      async startStream() {
        try {
          // Emit init promise for loading state (matching vue-qrcode-reader API)
          const initPromise = this.initCamera();
          this.$emit('init', initPromise);

          const stream = await initPromise;

          // Check if component was destroyed during async operation
          if (this.isDestroyed) {
            stream.getTracks().forEach((track) => track.stop());
            return;
          }

          this.currentStream = stream;

          // Check refs before accessing
          if (!this.$refs || !this.$refs.video) {
            stream.getTracks().forEach((track) => track.stop());
            return;
          }

          this.$refs.video.srcObject = stream;

          // Wait for video to be ready
          await new Promise((resolve) => {
            if (this.isDestroyed || !this.$refs || !this.$refs.video) {
              resolve();
              return;
            }
            this.$refs.video.onloadedmetadata = () => {
              if (!this.isDestroyed && this.$refs && this.$refs.video) {
                this.$refs.video.play();
              }
              resolve();
            };
          });

          // Check again after async operation
          if (this.isDestroyed) {
            return;
          }

          // Emit camera-on event with the stream (matching vue-qrcode-reader API)
          this.$emit('camera-on', stream);

          // Start scanning for QR codes
          this.startScanning();

        } catch (error) {
          // Only emit error if component is still alive
          if (!this.isDestroyed) {
            this.$emit('init', Promise.reject(error));
          }
        }
      },

      /**
       * Initialize camera with given constraints
       * @returns {Promise<MediaStream>}
       */
      async initCamera() {
        const constraints = this.constraints || { video: { facingMode: 'environment' } };
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        return stream;
      },

      /**
       * Stop the camera stream and scanning
       */
      stopStream() {
        this.stopScanning();

        if (this.currentStream) {
          this.currentStream.getTracks().forEach((track) => track.stop());
          this.currentStream = null;
        }

        // Check if component is still mounted before accessing refs
        if (this.$refs && this.$refs.video) {
          this.$refs.video.srcObject = null;
        }
      },

      /**
       * Start scanning frames for QR codes
       */
      startScanning() {
        if (this.isScanning) {
          return;
        }

        this.isScanning = true;
        this.scanInterval = setInterval(() => {
          this.scanFrame();
        }, 300); // Scan every 300ms
      },

      /**
       * Stop scanning for QR codes
       */
      stopScanning() {
        this.isScanning = false;
        if (this.scanInterval) {
          clearInterval(this.scanInterval);
          this.scanInterval = null;
        }
      },

      /**
       * Scan current video frame for QR codes
       */
      scanFrame() {
        // Check if component is destroyed
        if (this.isDestroyed || !this.$refs) {
          return;
        }

        const video = this.$refs.video;
        const canvas = this.$refs.canvas;

        if (!video || !canvas || video.readyState !== video.HAVE_ENOUGH_DATA) {
          return;
        }

        const context = canvas.getContext('2d');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        if (canvas.width === 0 || canvas.height === 0) {
          return;
        }

        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        const imageData = context.getImageData(0, 0, canvas.width, canvas.height);

        // Use jsQR to decode
        const code = this.decodeQRCode(imageData);

        if (code) {
          this.$emit('decode', code.data);
        }
      },

      /**
       * Decode QR code from image data using jsQR
       * @param {ImageData} imageData
       * @returns {Object|null}
       */
      decodeQRCode(imageData) {
        return jsQR(imageData.data, imageData.width, imageData.height, {
          inversionAttempts: 'dontInvert',
        });
      }
    },
    mounted() {
      this.startStream();
    },
    beforeDestroy() {
      this.isDestroyed = true;
      this.stopStream();
    },
    watch: {
      constraints: {
        deep: true,
        handler() {
          this.stopStream();
          this.$nextTick(() => {
            this.startStream();
          });
        }
      }
    }
  };
</script>

<style scoped>
  .qr-code-stream-wrapper {
    position: relative;
    width: 100%;
    background: #000;
  }

  .qr-video {
    width: 100%;
    height: auto;
    display: block;
  }

  .qr-canvas {
    display: none;
  }
</style>
